import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import SearchIcon from '@mui/icons-material/Search'
import {
    alpha,
    Autocomplete,
    Box,
    CircularProgress,
    InputAdornment,
    InputLabel,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    TextField,
    Typography,
    useMediaQuery
} from '@mui/material'
import { useTranslations } from 'next-intl'
import { useState } from 'react'
import SwipeableDrawerWithBackNavigation from '../../ui/swipeableDrawer'

interface ItineraryAutoCompleteProps<T extends Record<string, any>> {
    label: string
    options: T[]
    value: T | null
    onChange: (value: T | null) => void
    getOptionLabel?: (option: T) => string
    error?: boolean
    errorMessage?: string
    placeholder?: string
    loading?: boolean
    renderOption?: (props: React.HTMLAttributes<HTMLLIElement>, option: T) => React.ReactNode
    renderValue?: (value: T) => React.ReactNode
    optionLabelKey?: keyof T // key of the option to be used as the label
    onBlur?: () => void
    groupBy?: (option: T) => string
    disableClearable?: boolean
    renderValueForMobileScreen?: ((value: T) => string) | undefined
    readOnly?: boolean
}

const ItineraryAutoComplete = <T extends Record<string, any>>({
    label,
    options,
    value,
    onChange,
    getOptionLabel = option => (optionLabelKey ? option[optionLabelKey] : JSON.stringify(option)),
    error = false,
    errorMessage,
    placeholder = 'Select',
    loading = false,
    onBlur,
    renderOption = getOptionLabel
        ? (props: any, option) => (
              <Typography {...props} key={props.key} sx={{ fontSize: 13 }}>
                  {getOptionLabel(option)}
              </Typography>
          )
        : undefined,
    renderValue,
    optionLabelKey = 'title',
    groupBy,
    disableClearable = false,
    renderValueForMobileScreen,
    readOnly = false
}: ItineraryAutoCompleteProps<T>) => {
    const [drawerOpen, setDrawerOpen] = useState(false)
    const [searchText, setSearchText] = useState('')

    const t = useTranslations('common')
    // Check if this is mobile mode
    const isMobile = useMediaQuery(theme => theme.breakpoints.down('md'))

    // Filter options based on search text
    // Normalize search text and labels by removing spaces around "+" to handle "+966" vs "+ 966"
    const normalizeSearchText = (text: string) => {
        return text.toLowerCase().replace(/\+\s*/g, '+').replace(/\s+/g, ' ')
    }
    
    const filteredOptions = searchText
        ? options.filter(option => {
              const label = normalizeSearchText(getOptionLabel(option))
              const normalizedSearch = normalizeSearchText(searchText)
              return label.includes(normalizedSearch)
          })
        : options

    // Group options if groupBy is provided
    const groupedOptions = groupBy
        ? filteredOptions.reduce(
              (acc, option) => {
                  const group = groupBy(option)
                  if (!acc[group]) {
                      acc[group] = []
                  }
                  acc[group].push(option)
                  return acc
              },
              {} as Record<string, T[]>
          )
        : { '': filteredOptions }

    const handleSelectOption = (option: T) => {
        onChange(option)
        setDrawerOpen(false)
        setSearchText('')
    }

    // Mobile drawer UI
    if (isMobile) {
        return (
            <Box>
                <InputLabel
                    shrink
                    sx={{
                        fontWeight: 600,
                        fontSize: 16,
                        color: '#374151'
                    }}
                >
                    {label}
                </InputLabel>
                <TextField
                    size="small"
                    value={
                        value
                            ? renderValueForMobileScreen
                                ? renderValueForMobileScreen(value)
                                : getOptionLabel(value)
                            : ''
                    }
                    placeholder={placeholder}
                    onClick={() => !readOnly && setDrawerOpen(true)}
                    error={error}
                    onBlur={onBlur}
                    helperText={errorMessage}
                    sx={{
                        width: '100%',
                        '& .MuiOutlinedInput-root': {
                            height: 35
                        },
                        '& .MuiInputBase-input': {
                            py: 0.5,
                            px: 1.5,
                            fontSize: 13,
                            height: 26,
                            borderRadius: 1,
                            cursor: 'pointer'
                        },
                        borderRadius: 1
                    }}
                    InputProps={{
                        readOnly: true,
                        endAdornment: loading ? (
                            <CircularProgress color="inherit" size={20} />
                        ) : (
                            <ArrowDropDownIcon sx={{ fontSize: 24, color: 'action.active' }} />
                        )
                    }}
                />

                <SwipeableDrawerWithBackNavigation
                    anchor="bottom"
                    open={drawerOpen && !readOnly}
                    onClose={() => {
                        setDrawerOpen(false)
                        setSearchText('')
                    }}
                >
                    <Box sx={{ p: 2, pb: 0 }}>
                        {/* Search Bar */}
                        <TextField
                            fullWidth
                            size="small"
                            placeholder={`${t('search')} ${label.toLowerCase()}...`}
                            value={searchText}
                            onChange={e => setSearchText(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ fontSize: 20 }} />
                                    </InputAdornment>
                                )
                            }}
                            sx={{
                                mb: 2,
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2
                                }
                            }}
                        />
                    </Box>

                    {/* Options List */}
                    <List
                        sx={{
                            overflow: 'auto',
                            px: 1
                        }}
                    >
                        {loading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                                <CircularProgress />
                            </Box>
                        ) : (
                            Object.entries(groupedOptions).map(([group, groupOptions]) => (
                                <Box key={group}>
                                    {group && (
                                        <Box
                                            sx={{
                                                padding: '4px 10px',
                                                backgroundColor: theme => alpha(theme.palette.primary.main, 0.3),
                                                color: 'primary.main',
                                                fontWeight: 600,
                                                fontSize: 13,
                                                lineHeight: '36px'
                                            }}
                                        >
                                            {group}
                                        </Box>
                                    )}
                                    {groupOptions.map((option, index) => (
                                        <ListItem key={index} disablePadding>
                                            <ListItemButton
                                                onClick={() => handleSelectOption(option)}
                                                selected={!!(value && getOptionLabel(value) === getOptionLabel(option))}
                                                sx={{
                                                    '&.Mui-selected': {
                                                        backgroundColor: theme =>
                                                            alpha(theme.palette.primary.main, 0.08)
                                                    }
                                                }}
                                            >
                                                <ListItemText
                                                    primary={getOptionLabel(option)}
                                                    primaryTypographyProps={{ fontSize: 14 }}
                                                />
                                            </ListItemButton>
                                        </ListItem>
                                    ))}
                                </Box>
                            ))
                        )}
                    </List>
                </SwipeableDrawerWithBackNavigation>
            </Box>
        )
    }

    // Desktop autocomplete UI (existing implementation)
    return (
        <Box>
            <InputLabel
                shrink
                sx={{
                    fontWeight: 600,
                    fontSize: 16,
                    color: '#374151'
                }}
            >
                {label}
            </InputLabel>
            <Autocomplete
                readOnly={readOnly}
                groupBy={groupBy}
                renderGroup={params => (
                    <li key={params.key}>
                        <Box
                            sx={{
                                position: 'sticky',
                                top: '-8px',

                                color: 'primary.main',
                                backgroundColor: '#fff',
                                fontSize: 13
                            }}
                        >
                            <Box
                                sx={{
                                    padding: '4px 10px',
                                    backgroundColor: theme => alpha(theme.palette.primary.main, 0.3)
                                }}
                            >
                                {params.group}
                            </Box>
                        </Box>
                        <Typography component={'span'} sx={{ fontSize: 13 }}>
                            {params.children}
                        </Typography>
                    </li>
                )}
                loading={loading}
                options={options}
                value={value || null}
                getOptionLabel={getOptionLabel}
                onBlur={onBlur}
                onChange={(_, newValue) => {
                    onChange(newValue)
                }}
                renderOption={renderOption}
                renderValue={renderValue}
                disableClearable={disableClearable}
                filterOptions={(options, { inputValue }) => {
                    // Normalize search text and labels by removing spaces around "+" to handle "+966" vs "+ 966"
                    const normalizedInput = normalizeSearchText(inputValue)
                    return options.filter(option => {
                        const label = normalizeSearchText(getOptionLabel(option))
                        return label.includes(normalizedInput)
                    })
                }}
                renderInput={params => (
                    <TextField
                        {...params}
                        size="small"
                        placeholder={value ? '' : placeholder}
                        error={error}
                        helperText={errorMessage}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                height: 35
                            },
                            '& .MuiInputBase-input': {
                                py: 0.5,
                                px: 1.5,
                                fontSize: 13,
                                height: 26,
                                borderRadius: 1
                            },
                            borderRadius: 1
                        }}
                        slotProps={{
                            input: {
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                        {params.InputProps.endAdornment}
                                    </>
                                )
                            }
                        }}
                    />
                )}
                slotProps={{
                    paper: {
                        sx: {
                            minWidth: '220px'
                        }
                    }
                }}
                sx={{
                    '& .MuiAutocomplete-popupIndicator': {
                        transform: 'none'
                    }
                }}
            />
        </Box>
    )
}

export default ItineraryAutoComplete
